import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useState, useEffect } from 'react'
import { Filter } from '../../../models/filters'
import { FilterCategoryType, FilterSubcategoryItemProps } from '@/app/models/filters'
import { canViewReports } from '@Techedge/digitalportal-shared'
import FilterSubCategoryItem from '../FilterSubCategoryItem/FilterSubCategoryItem'
import {
    Color,
    LuxDatePicker,
    LuxDateRangePicker,
    LuxSelect,
    LuxTextInput,
    LUX_INPUT_TYPE,
    LuxIcon,
    useWindowResize,
} from '@lux/wardrobe-lib'
import './FilterSubCategories.scss'

type Props = {
    filterList: FilterSubcategoryItemProps[]
    filterCategorySelected: FilterCategoryType
    language: string
    localEditedFilter?: Filter
    changeEditedFilterField?: any
    reportTimes?: { value: string; label: string }[]
    selectedReportTime?: string
    onChangeReportsTiming?: (newVal: string) => void
}

const FilterSubCategories = ({
    filterList,
    filterCategorySelected,
    localEditedFilter,
    changeEditedFilterField,
    reportTimes,
    selectedReportTime,
    onChangeReportsTiming,
    language,
}: Props) => {
    const intl = useIntl()
    if (filterCategorySelected === 'PROJECTS') {
        return (
            <div className="filter-subcategories-container">
                <div className="search-input-container">
                    <LuxTextInput
                        type={LUX_INPUT_TYPE.text}
                        value={localEditedFilter?.categories?.project?.projectTitle ?? ''}
                        placeholder={intl.formatMessage({
                            id: 'modals.filters.placeholder.search_project_here',
                            defaultMessage: 'Search Project Here',
                        })}
                        onChangeCallback={(value) => {
                            changeEditedFilterField(value, 'categories.project.projectTitle')
                        }}
                        icon={'search'}
                        iconPosition={'left'}
                        label={intl.formatMessage({
                            id: 'modals.filters.label.project_search',
                            defaultMessage: 'Project Search',
                        })}
                    />
                </div>
                {filterList?.map((item) => (
                    <FilterSubCategoryItem key={item.label} {...item} />
                ))}
            </div>
        )
    }
    if (filterCategorySelected === 'STORE_SPECS' || filterCategorySelected === 'PHOTO_SPECS') {
        return (
            <div className="filter-subcategories-container filter-subcategories-store-specs-container">
                {filterList?.map((item) => (
                    <FilterSubCategoryItem key={item.label} {...item} />
                ))}
            </div>
        )
    }
    if (filterCategorySelected === 'TIME_FRAME') {
        return (
            <FilterTimeFrameContent
                localEditedFilter={localEditedFilter}
                changeEditedFilterField={changeEditedFilterField}
                reportTimes={reportTimes}
                selectedReportTime={selectedReportTime}
                onChangeReportsTiming={onChangeReportsTiming}
                language={language}
            />
        )
    }
    return <></>
}

const FilterTimeFrameContent = (props: any) => {
    const {
        localEditedFilter,
        changeEditedFilterField,
        reportTimes,
        selectedReportTime,
        onChangeReportsTiming,
    } = props
    // we convert all the dates to timestamp to allow comparisons and avoid loops
    const intl = useIntl()

    const isMobile = useWindowResize()

    const [startDate, setStartDate] = useState<number>(
        localEditedFilter?.categories?.timeFrame.from || undefined
    )
    const [endDate, setEndDate] = useState<number>(
        localEditedFilter?.categories?.timeFrame.to || undefined
    )
    const [maxRangeDate, setMaxRangeDate] = useState<Date>(undefined)
    const [minRangeDate, setMinRangeDate] = useState<Date>(undefined)
    const [timeFrame, setTimeFrame] = useState(
        localEditedFilter?.categories?.timeFrame.from && localEditedFilter?.categories?.timeFrame.to
            ? {
                  start: new Date(localEditedFilter?.categories?.timeFrame.from),
                  end: new Date(localEditedFilter?.categories?.timeFrame.to),
              }
            : { start: null, end: null }
    )
    // const [activelyChangingRange, setActivelyChangingRange] = useState(false)
    const [isPickerStartVisible, setIsPickerStartVisible] = useState(false)
    const [isPickerEndVisible, setIsPickerEndVisible] = useState(false)
    const [isPickerFromToVisible, setIsPickerFromToVisible] = useState(false)

    const onFocus = () => {
        setIsPickerStartVisible(true)
    }
    const onFocusEnd = () => {
        setIsPickerEndVisible(true)
    }
    const onFocusFromTo = () => {
        setIsPickerFromToVisible(true)
    }

    const onVisibleChange = (e) => {}

    useEffect(() => {
        const pickerIconStart = document
            .getElementsByClassName('filter-subcategories-container__picker start')[0]
            .getElementsByClassName('p-datepicker-trigger')[0]
        pickerIconStart.addEventListener('click', () =>
            setIsPickerStartVisible(!document.getElementsByClassName('start-picker')[0])
        )
        const pickerIconEnd = document
            .getElementsByClassName('filter-subcategories-container__picker end')[0]
            .getElementsByClassName('p-datepicker-trigger')[0]
        pickerIconEnd.addEventListener('click', () =>
            setIsPickerEndVisible(!document.getElementsByClassName('end-picker')[0])
        )
        const pickerIconFromTo = document
            .getElementsByClassName('filter-subcategories-container__picker from-to')[0]
            .getElementsByClassName('p-datepicker-trigger')[0]
        pickerIconFromTo.addEventListener('click', () =>
            setIsPickerFromToVisible(!document.getElementsByClassName('from-to-picker')[0])
        )
        /**
         * Alert if clicked on outside of element
         */
        function handleClickOutside(event) {
            const pickerStart = document.getElementsByClassName('start-picker')[0]
            const pickerEnd = document.getElementsByClassName('end-picker')[0]
            const pickerFromTo = document.getElementsByClassName('from-to')[0]
            if (!!pickerStart && !pickerStart.contains(event.target)) {
                setIsPickerStartVisible(false)
            }
            if (!!pickerEnd && !pickerEnd.contains(event.target)) {
                setIsPickerEndVisible(false)
            }
            if (!!pickerFromTo && !pickerFromTo.contains(event.target)) {
                setIsPickerFromToVisible(false)
            }
        }
        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        let _start = localEditedFilter?.categories?.timeFrame.from || undefined
        let _end = localEditedFilter?.categories?.timeFrame.to || undefined
        // when timeframe comes from outside the component

        // if start and/or end are updated (es. a new filter is selected)
        if (_start !== startDate) {
            setStartDate(_start)
        }
        if (_end !== endDate) {
            setEndDate(_end)
        }
    }, [localEditedFilter?.categories?.timeFrame])

    useEffect(() => {
        let _start = localEditedFilter?.categories?.timeFrame.from || undefined
        let _end = localEditedFilter?.categories?.timeFrame.to || undefined
        // when dates are changed inside the inputs
        if (_start !== startDate || _end !== endDate)
            changeEditedFilterField({ from: startDate, to: endDate }, 'categories.timeFrame')
        if (startDate && endDate) {
            setTimeFrame({ start: new Date(startDate), end: new Date(endDate) })
        } else {
            setTimeFrame({ start: null, end: null })
        }
        if (startDate) {
            const ninetyDaysAfter = new Date(startDate)
            ninetyDaysAfter.setDate(ninetyDaysAfter.getDate() + 90)
            setMaxRangeDate(ninetyDaysAfter)
        } else {
            setMaxRangeDate(undefined)
        }
        if (endDate) {
            const ninetyDaysAgo = new Date(endDate)
            ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90)
            setMinRangeDate(ninetyDaysAgo)
        } else {
            setMinRangeDate(undefined)
        }
    }, [startDate, endDate])

    useEffect(() => {
        if (timeFrame.start && timeFrame.end) {
            const _start = timeFrame.start.getTime()
            const _end = timeFrame.end.getTime()
            if (_start !== startDate && _end !== endDate) {
                setStartDate(_start)
                setEndDate(_end)
            }
            setIsPickerFromToVisible(false)
        }
        // else if (activelyChangingRange) {
        //     setStartDate(undefined)
        //     setEndDate(undefined)
        // }
    }, [timeFrame])

    return (
        <div className="filter-subcategories-container">
            {isMobile ? (
                <h2 className="title">Time</h2>
            ) : (
                <h2 className="title">
                    {intl.formatMessage({
                        id: 'modals.filters.label.time_frame',
                        defaultMessage: 'Time Frame',
                    })}
                </h2>
            )}
            <div className="time-frame-disclaimer">
                <LuxIcon icon="info" size={16} color={Color.GREY1} classes="mr-2" />
                <span className={isMobile ? 'text-mini' : 'text'}>
                    {intl.formatMessage({
                        id: 'modals.filters.month-range.disclaimer',
                        defaultMessage:
                            'The maximum range of searching and date filtering is 3 months. Without filters or time range set, default values are applied.',
                    })}
                </span>
            </div>
            <div className="input-container">
                <div className="spacing-date-input p-inputwrapper">
                    <LuxDatePicker
                        intl={intl}
                        locale={props.language?.split('-')[0] || 'en'}
                        label={intl.formatMessage({
                            id: 'modals.filters.label.start_date',
                            defaultMessage: 'Start Date',
                        })}
                        placeholder={intl.formatMessage({
                            id: 'modals.filters.placeholder.start_date',
                            defaultMessage: 'Start Date',
                        })}
                        value={startDate ? new Date(startDate) : undefined}
                        minDate={minRangeDate}
                        maxDate={endDate ? new Date(endDate) : undefined}
                        onChangeCallback={(val) => {
                            setIsPickerStartVisible(false)
                            const _timestamp = val ? (val as Date).getTime() : undefined
                            if (_timestamp !== startDate) {
                                // setActivelyChangingRange(true)
                                setStartDate(_timestamp)
                            }
                        }}
                        style={{ backgroundColor: Color.WHITE }}
                        sideView={true}
                        containerClasses={`filter-subcategories-container__picker start`}
                        additionalProps={{
                            panelClassName: 'smart-wardrobe-datepicker start-picker',
                            appendTo: 'self',
                        }}
                        showCustom
                        visible={isPickerStartVisible}
                        onFocus={onFocus}
                        onVisibleChange={onVisibleChange}
                        dateFormat={props.language?.split('-')[0] === 'tr' ? 'dd/mm/yy' : undefined}
                    />
                </div>
                <div className="spacing-date-input p-inputwrapper">
                    <LuxDatePicker
                        intl={intl}
                        locale={props.language?.split('-')[0] || 'en'}
                        label={intl.formatMessage({
                            id: 'modals.filters.label.end_date',
                            defaultMessage: 'End Date',
                        })}
                        placeholder={intl.formatMessage({
                            id: 'modals.filters.placeholder.end_date',
                            defaultMessage: 'End Date',
                        })}
                        value={endDate ? new Date(endDate) : undefined}
                        minDate={startDate ? new Date(startDate) : undefined}
                        maxDate={maxRangeDate}
                        onChangeCallback={(val) => {
                            setIsPickerEndVisible(false)
                            const _timestamp = val ? (val as Date).getTime() : undefined
                            if (_timestamp !== endDate) {
                                // setActivelyChangingRange(true)
                                setEndDate(_timestamp)
                            }
                        }}
                        style={{ backgroundColor: Color.WHITE }}
                        sideView={true}
                        containerClasses={`filter-subcategories-container__picker end`}
                        additionalProps={{
                            panelClassName: 'smart-wardrobe-datepicker end-picker',
                            appendTo: 'self',
                        }}
                        visible={isPickerEndVisible}
                        onFocus={onFocusEnd}
                        onVisibleChange={onVisibleChange}
                        showCustom
                        dateFormat={props.language?.split('-')[0] === 'tr' ? 'dd/mm/yy' : undefined}
                    />
                </div>
                <div className="spacing-date-input p-inputwrapper date-range">
                    <LuxDateRangePicker
                        intl={intl}
                        locale={props.language?.split('-')[0] || 'en'}
                        label={intl.formatMessage({
                            id: 'modals.filters.label.from_to',
                            defaultMessage: 'From - To',
                        })}
                        placeholder={intl.formatMessage({
                            id: 'modals.filters.placeholder.from_to',
                            defaultMessage: 'From - To',
                        })}
                        disabled={false}
                        mandatory={false}
                        floatingLabel={false}
                        singleInput
                        maxDaysRange={90}
                        value={timeFrame}
                        onChangeCallback={(range) => {
                            if (
                                range.start?.getTime() !== timeFrame.start?.getTime() ||
                                range.end?.getTime() !== timeFrame.end?.getTime()
                            ) {
                                setTimeFrame(range)
                                // setActivelyChangingRange(true)
                            }
                            // else setActivelyChangingRange(false)
                        }}
                        sideView={true}
                        additionalProps={{
                            panelClassName: 'smart-wardrobe-datepicker from-to-picker',
                            appendTo: 'self',
                        }}
                        containerClasses={`filter-subcategories-container__picker from-to`}
                        style={{ backgroundColor: Color.WHITE }}
                        dateFormat={props.language?.split('-')[0] === 'tr' ? 'dd/mm/yy' : undefined}
                    />
                </div>
                {canViewReports() && (
                    <div className="spacing-date-input p-inputwrapper reports-timing-select">
                        <LuxSelect
                            options={reportTimes}
                            value={selectedReportTime}
                            onChangeCallback={onChangeReportsTiming}
                            label={intl.formatMessage({
                                id: 'modals.filters.reports_timing.label',
                                defaultMessage: 'Time cluster',
                            })}
                            placeholder={intl.formatMessage({
                                id: 'modals.filters.reports_timing.option.none',
                                defaultMessage: 'None',
                            })}
                        />
                        <div className="reports-timing-text">
                            {intl.formatMessage({
                                id: 'modals.filters.reports_timing.disclaimer',
                                defaultMessage:
                                    'Note: this filter acts only on content inside the profile management > reports section',
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

const mapStateToProps = (state: any) => ({ language: state.userReducer.user.language })

const mapDispatchToProps = (dispatch: any) => ({})

export default connect(mapStateToProps, mapDispatchToProps)(FilterSubCategories)
